// Shared helpers for the Advisor Desk — stage classification, formatting and
// the visual language used across the list, detail panel and customer view.

export const STAGES = {
  waiting: { label: "Waiting", dot: "bg-slate-400", text: "text-slate-300", ring: "ring-slate-600" },
  in_bay: { label: "In a bay", dot: "bg-sky-400", text: "text-sky-300", ring: "ring-sky-500/50" },
  workshop_complete: { label: "Tech done", dot: "bg-pink-400", text: "text-pink-300", ring: "ring-pink-500/50" },
  ready: { label: "Ready", dot: "bg-emerald-400", text: "text-emerald-300", ring: "ring-emerald-500/50" },
  collected: { label: "Collected", dot: "bg-slate-500", text: "text-slate-400", ring: "ring-slate-700" },
};

export function stageOf(job) {
  if (job.completed_at) return "collected";
  if (job.ready_for_collection_at) return "ready";
  if (job.workshop_complete_at) return "workshop_complete";
  if (job.in_bay_at) return "in_bay";
  return "waiting";
}

const QUOTE_LABELS = {
  draft: "Not sent",
  sent: "Awaiting reply",
  viewed: "Opened, no answer",
  partial: "Part approved",
  approved: "All approved",
  rejected: "Declined",
  completed: "Completed",
};

export function quoteLabel(job) {
  return QUOTE_LABELS[job.status] || job.status || "—";
}

// A car is "chaseable" when the customer has the quote but hasn't finished
// deciding — the advisor's whole morning is these cars.
export function isAwaitingApproval(job) {
  return ["sent", "viewed", "partial"].includes(job.status);
}

export function money(n) {
  const v = Number(n || 0);
  return `£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

// "3h 20m" since a timestamp — how long the customer has been sitting on it.
export function ageSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

const WORK_TYPE_SHORT = {
  small_service: "Service", large_service: "Large service", mot: "MOT",
  brakes: "Brakes", diagnostic: "Diagnostic", tyres: "Tyres", aircon: "Air-con",
  wheel_alignment: "Alignment", cambelt: "Cambelt", clutch: "Clutch",
  battery: "Battery", wipers: "Wipers", bulbs: "Bulbs", recall: "Recall",
};

export function workLabel(job) {
  const types = (job.work_types || [])
    .map((t) => WORK_TYPE_SHORT[String(t || "").toLowerCase()] || String(t || "").replace(/_/g, " "))
    .filter(Boolean);
  if (types.length) return types.join(" + ");
  if (job.booked_service_name) return String(job.booked_service_name);
  if (job.job_type === "diagnostic") return "Diagnostic";
  if (job.work_description) return String(job.work_description).slice(0, 48);
  return "Booked in";
}

export function vehicleLabel(job) {
  return [job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ") || "Vehicle";
}

export function isToday(job) {
  const today = new Date().toISOString().slice(0, 10);
  return (job.appointment_date || job.appointment_at || job.created_at || "").slice(0, 10) === today;
}

// Outstanding = quoted work the customer hasn't said yes to yet.
export function outstanding(job) {
  return Math.max(0, Number(job.subtotal || 0) - Number(job.approved_total || 0));
}
